import React, { useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { removeBasketInstrumentAction } from '../../../store/actions/basket';
import { openOrderWindow } from '../../../store/actions/orders';
import { useSnackbar } from 'notistack';
import BasketInstrumentRow from './BasketInstrumentRow';
import BasketMarginSummary from './BasketMarginSummary';
import { useCallback } from 'react';
import { styles } from './BasketDetailsCSS';

const BasketDetails = ({ basketId, basketName, instruments, isExecuted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [instrumentToDelete, setInstrumentToDelete] = useState(null);
  
  // Cache margins so rows don't lose them when remounting on screen resize
  const marginsCacheRef = useRef({});
  const ltpMapRef = useRef({});
  const onTickCallbackRef = useRef(null);

  const handleLtpUpdate = useCallback((id, data) => {
    ltpMapRef.current[id] = data;
    if (onTickCallbackRef.current) {
      onTickCallbackRef.current();
    }
  }, []);

  const handleDeleteClick = (instrumentId, e) => {
    if (e) e.stopPropagation();
    setInstrumentToDelete(instrumentId);
  };

  const handleConfirmDelete = async () => {
    if (!instrumentToDelete) return;
    
    const payload = {
      basket_id: basketId,
      order_id: instrumentToDelete,
      name: basketName
    };
    
    const res = await dispatch(removeBasketInstrumentAction(payload));
    if (res.status === 'success') {
      enqueueSnackbar('Instrument removed from basket', { variant: 'success' });
    } else {
      enqueueSnackbar(res.message || 'Failed to remove instrument', { variant: 'error' });
    }
    
    setInstrumentToDelete(null);
  };

  const handleCloseDeleteDialog = () => {
    setInstrumentToDelete(null);
  };

  const handleModifyInstrument = (instrument, e) => {
    if (e) e.stopPropagation();
    
    // Format the instrument data for the OrderWindow
    const orderData = {
      is_modify: true,
      source: "MODIFY_BASKET",
      basketDetails: {
        basketId: basketId,
        basketName: basketName,
        id: instrument.id // The specific leg ID to modify
      },
      token: instrument.instrument_token,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      order_type: instrument.order_type,
      product: instrument.product,
      quantity: instrument.quantity,
      price: instrument.price,
      trigger_price: instrument.trigger_price,
      type: instrument.order_side,
      execution: instrument.execution_type,
      instrument_name: instrument.series
    };
    
    dispatch(openOrderWindow(orderData));
  };



  if (!instruments || instruments.length === 0) {
    return (
      <Box sx={styles.emptyStateContainer}>
        <Typography color="text.secondary">This basket is empty.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.mainContainer}>
      {/* Total Margin Summary */}
      <BasketMarginSummary 
        instruments={instruments} 
        ltpMapRef={ltpMapRef} 
        onTickCallbackRef={onTickCallbackRef} 
        marginsCacheRef={marginsCacheRef}
      />

      {isMobile ? (
        <Box sx={styles.mobileListContainer}>
          {instruments.map((instrument) => (
            <BasketInstrumentRow 
              key={instrument.id}
              isMobile={true}
              instrument={instrument}
              basketId={basketId}
              onModify={handleModifyInstrument}
              onDelete={handleDeleteClick}
              isExecuted={isExecuted}
              marginsCacheRef={marginsCacheRef}
              onLtpUpdate={handleLtpUpdate}
            />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={styles.desktopTableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={styles.tableHeaderCell}>Scrip Name</TableCell>
                <TableCell sx={styles.tableHeaderCell}>LTP</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Order Side</TableCell>
                <TableCell align="center" sx={styles.tableHeaderCell}>Qty</TableCell>
                <TableCell align="center" sx={styles.tableHeaderCell}>Order Price</TableCell>
                <TableCell align="center" sx={styles.tableHeaderCell}>Req. Margin</TableCell>
                <TableCell align="center" sx={styles.tableHeaderCell}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instruments.map((instrument) => (
                <BasketInstrumentRow 
                  key={instrument.id}
                  instrument={instrument}
                  basketId={basketId}
                  onModify={handleModifyInstrument}
                  onDelete={handleDeleteClick}
                  isExecuted={isExecuted}
                  marginsCacheRef={marginsCacheRef}
                  onLtpUpdate={handleLtpUpdate}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(instrumentToDelete)}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this instrument from the basket?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BasketDetails;
